/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useState } from 'react'
import { Input } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import BaseModal from 'src/components/ts-components/BaseModal'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import { Checkbox } from 'src/components/ts-components/Checkbox'
import Text from 'src/components/ts-components/Text'
import { paragraphXSmall } from 'src/components/css'
import { balanceLabel, balanceText } from '../utils/balanceI18n'

const MAX_SUBJECT = 255
const MAX_MESSAGE = 20000
/** The reminder endpoint's own message cap — the tighter of the two. */
const MAX_REMINDER_MESSAGE = 500

/**
 * How many names to show before collapsing the rest into "+N more". A roster can
 * be 60 deep, and 60 chips inside a dialog is the same "I can't read this"
 * problem as the bare count it replaces.
 */
const MAX_VISIBLE_RECIPIENTS = 12

export type NotifyChannel = 'reminder' | 'post'

export interface NotifyComposerBody {
  channels: NotifyChannel[]
  subject: string
  message: string
}

type NotifyComposerModalProps = {
  open: boolean
  /**
   * Per-slot mode: the player a row's Remind button targets. Non-null narrows
   * the composer to the reminder channel alone — the feed post is a TEAM-wide
   * broadcast, and offering it from one player's row would notify the whole
   * roster from a control that names one person.
   */
  playerName?: string | null
  /** The bulk audience, by name. Advisory — the server recomputes on send. */
  recipients?: string[]
  count?: number
  /** True when the audience is the grid's row SELECTION rather than everyone. */
  selectedOnly?: boolean
  /** The feed `post` ability — a DIFFERENT Bouncer gate from manageBalance. */
  canPost?: boolean
  /** No remindable slot in the audience ⇒ the reminder channel is unavailable. */
  canRemind?: boolean
  submitting: boolean
  onConfirm: (body: NotifyComposerBody) => void
  onClose: () => void
}

/**
 * SN-4709 (James, Aug 12) — ONE composer, several channels.
 *
 * "If you select here and then notify → remind selected… I think this would just
 * give you the option to post in the channel. By posts, by email. So it should
 * just give you the options. It's just one mechanism and you can do multiple."
 *
 * This replaces three separate modals behind three separate menu items
 * (ReminderConfirmModal / EmailTeamModal / PostNotifyModal). They asked the same
 * two questions — who, and what do you want to say — and then differed only in
 * which door you had come through.
 *
 * The TRANSPORT is deliberately NOT merged: the endpoints keep their own result
 * shapes and, crucially, their own authorization. The feed post is gated on the
 * `post` ability rather than `manageBalance`, which is why its checkbox is
 * conditional and cannot be inferred from the others (the server stays
 * authoritative regardless).
 *
 * LAYOUT follows FeedPostCreateModal (Inon, Aug 12: "it should be more
 * presentable like that") — sentence-case section labels, hairline rules between
 * sections, and the house `Checkbox` with its label + hint, so a channel says
 * what it actually does instead of relying on the reader to guess. The audience
 * is CHIPS in a bordered field rather than a bulleted list in a grey box, which
 * read as a debug dump.
 *
 * There is no "email everyone on the roster" channel: the reminder already
 * emails, and a second wider door was a way to spam a roster by accident.
 */
const NotifyComposerModal = ({
  open,
  playerName = null,
  recipients = [],
  count = 0,
  selectedOnly = false,
  canPost = false,
  canRemind = true,
  submitting,
  onConfirm,
  onClose,
}: NotifyComposerModalProps) => {
  const isSlot = playerName !== null
  const [channels, setChannels] = useState<NotifyChannel[]>([])
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')

  // Each open starts clean — a note typed for one audience must never carry over
  // to another, and neither must a channel ticked for it.
  useEffect(() => {
    if (open) {
      setSubject('')
      setMessage('')
      // The reminder is what "Notify" means by default; the feed post is opt-in,
      // because it reaches people who owe nothing.
      setChannels(canRemind ? ['reminder'] : [])
    }
  }, [open, canRemind])

  const visible = recipients.slice(0, MAX_VISIBLE_RECIPIENTS)
  const overflow = recipients.length - visible.length

  const wants = (channel: NotifyChannel) => channels.includes(channel)
  const toggle = (channel: NotifyChannel) => (checked: boolean) =>
    setChannels((prev) =>
      checked ? [...prev, channel] : prev.filter((item) => item !== channel)
    )

  // Subject + message are REQUIRED by the feed-post endpoint and optional on a
  // reminder, so the send gate follows what is actually ticked.
  const needsComposition = wants('post')
  const canSend =
    channels.length > 0 &&
    (!needsComposition ||
      (subject.trim().length > 0 && message.trim().length > 0))

  const messageLimit = needsComposition ? MAX_MESSAGE : MAX_REMINDER_MESSAGE

  const sectionLabel = (text: string) => (
    <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
      {text}
    </Text>
  )

  return (
    <BaseModal
      title={balanceLabel(
        isSlot ? 'common:balanceNotifySlotTitle' : 'common:balanceNotifyTitle'
      )}
      isOpen={open}
      onClose={onClose}
      desktopWidth='620px'
      destroyOnClose
      actions={
        <div css={footerStyle}>
          <HeaderActionButton
            variant='secondary'
            size='small'
            textWeight='regular'
            text={balanceLabel('common:balanceCommsCancel')}
            onClick={onClose}
            disabled={submitting}
            data-testid='notify-cancel'
          />
          <HeaderActionButton
            variant='primary'
            size='small'
            textWeight='regular'
            text={balanceLabel('common:balanceNotifySend')}
            onClick={() =>
              onConfirm({
                channels,
                subject: subject.trim(),
                message: message.trim(),
              })
            }
            isLoading={submitting}
            disabled={!canSend}
            data-testid='notify-submit'
          />
        </div>
      }
    >
      <p css={bodyStyle} data-testid='notify-body'>
        {isSlot
          ? balanceText('common:balanceNotifySlotBody', { name: playerName })
          : balanceText(
              selectedOnly
                ? 'common:balanceNotifySelectedBody'
                : 'common:balanceNotifyBody',
              { count }
            )}
      </p>
      {/*
        SN-8347 — the feed post is NOT scoped to the players being reminded: the post is
        public and Admin / Player / Team Official / Following all see it. Said here, in a
        paragraph, rather than only in the channel's hint — the house Checkbox clamps a
        hint to two lines and was truncating this exact clause.

        Gated on the checkbox actually being OFFERED, not on it being ticked: a rep
        without the `post` ability has no feed control on screen, and warning them about
        a broadcast they cannot make would only discourage a legitimate reminder.
      */}
      {!isSlot && canPost ? (
        <p css={bodyStyle} data-testid='notify-feed-scope'>
          {balanceText('common:balanceNotifyFeedScopeNote')}
        </p>
      ) : null}

      {/* Only the bulk modes have an audience to preview — a per-slot notice
          already names its one recipient in the sentence above. */}
      {!isSlot && recipients.length > 0 ? (
        <div css={sectionStyle}>
          {sectionLabel(balanceLabel('common:balanceReminderRecipients'))}
          {/*
            Chips in a bordered field, the same shape the feed composer gives its
            Target Audience. A bulleted list in a grey box read as a dump of
            data rather than as a list of people.
          */}
          <div css={chipFieldStyle} data-testid='notify-recipients'>
            {visible.map((name, index) => (
              <span css={chipStyle} key={`${name}-${index}`}>
                {name}
              </span>
            ))}
            {overflow > 0 ? (
              <span css={[chipStyle, overflowChipStyle]}>
                {balanceText('common:balanceReminderRecipientsMore', {
                  count: overflow,
                })}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      <span css={ruleStyle} aria-hidden='true' />

      {/*
        The channels. Not a mode switch — several may be ticked, which is the
        whole point ("it's just one mechanism and you can do multiple"). Each
        says what it actually does; "Post to the team feed" alone does not tell a
        rep that the whole team sees it.
      */}
      <div css={sectionStyle}>
        {sectionLabel(balanceLabel('common:balanceNotifyChannels'))}
        <div css={channelGridStyle} data-testid='notify-channels'>
          {/*
            SN-8352 — on the single-player path the feed channel is withheld
            (below), so the reminder is the ONLY delivery there is. A lone
            checkbox therefore implied a choice that does not exist: ticking it
            changed nothing and unticking it could only disable Send. State the
            delivery instead of offering it.

            The reminder channel itself is untouched — it is still in `channels`
            (set on open) and still travels in the onConfirm payload, so the
            send is byte-identical to what the checkbox produced when ticked.
          */}
          {isSlot ? (
            // Withheld when there is nothing to remind about — the unavailable hint
            // below is the whole message then, and describing a delivery that cannot
            // happen directly above "no balance to remind" simply contradicts it.
            canRemind ? (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                <span data-testid='notify-channel-static'>
                  {balanceText('common:balanceNotifySlotChannel')}
                </span>
              </Text>
            ) : null
          ) : (
            <div data-testid='notify-channel-reminder'>
              <Checkbox
                name='notify-channel-reminder'
                label={balanceText('common:balanceNotifyChannelReminder')}
                hint={balanceText('common:balanceNotifyChannelReminderHint')}
                disabled={!canRemind}
                value={wants('reminder')}
                onChange={(event) => toggle('reminder')(event.target.checked)}
              />
            </div>
          )}
          {/* `post` is a different Bouncer ability from manageBalance, so this
              cannot be inferred from the caller being able to notify at all. A
              team-wide broadcast also has no meaning aimed at a single row. */}
          {!isSlot && canPost ? (
            <div data-testid='notify-channel-post'>
              <Checkbox
                name='notify-channel-post'
                label={balanceText('common:balanceNotifyChannelPost')}
                hint={balanceText('common:balanceNotifyChannelPostHint')}
                value={wants('post')}
                onChange={(event) => toggle('post')(event.target.checked)}
              />
            </div>
          ) : null}
        </div>
        {!canRemind ? (
          <span css={hintStyle} data-testid='notify-reminder-unavailable'>
            {balanceText('common:balanceRemindUnpaidDisabled')}
          </span>
        ) : null}
      </div>

      <span css={ruleStyle} aria-hidden='true' />

      {/* Subject is only read by the feed-post endpoint; a reminder has none, so
          asking for one when nothing needs it is a dead field. */}
      {needsComposition ? (
        <div css={sectionStyle}>
          {sectionLabel(balanceLabel('common:balanceEmailSubjectLabel'))}
          <Input
            id='balance-notify-subject'
            aria-label={balanceText('common:balanceEmailSubjectLabel')}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            maxLength={MAX_SUBJECT}
            data-testid='notify-subject'
          />
        </div>
      ) : null}

      <div css={sectionStyle}>
        {sectionLabel(
          balanceLabel(
            needsComposition
              ? 'common:balanceEmailMessageLabel'
              : 'common:balanceReminderMessageLabel'
          )
        )}
        {/*
          antd renders showCount's "n / max" ABSOLUTELY at bottom: -22px — below
          the textarea — and this is the last body element, so without reserved
          space the counter collides with the footer's Send button.
        */}
        <div css={messageWrapStyle}>
          <Input.TextArea
            id='balance-notify-message'
            aria-label={balanceText('common:balanceReminderMessageLabel')}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            maxLength={messageLimit}
            showCount
            autoSize={{ minRows: 3, maxRows: 8 }}
            placeholder={
              needsComposition
                ? undefined
                : balanceText('common:balanceReminderMessagePlaceholder')
            }
            data-testid='notify-message'
          />
        </div>
      </div>
    </BaseModal>
  )
}

export default NotifyComposerModal

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

// Reserve the band antd's absolute showCount counter occupies (~22px below the
// textarea) so "n / max" never overlaps the modal footer.
const messageWrapStyle = css`
  padding-bottom: 22px;
`

const bodyStyle = css`
  margin-bottom: 16px;
  color: ${colors.NEUTRAL_30};
`

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

/** The seam between sections, as the feed composer draws it. */
const ruleStyle = css`
  display: block;
  height: 1px;
  margin: 16px 0;
  background: ${colors.SECONDARY_100};
`

/**
 * The audience as a FIELD, matching the feed composer's Target Audience: a
 * bordered well the names sit inside, rather than a grey box with a bulleted
 * list in it. Scrolls internally — the audience can be the whole roster, and a
 * dialog whose confirm button is below the fold is worse than one that does not
 * list names at all.
 */
const chipFieldStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  background: ${colors.SECONDARY_200};
  max-height: 132px;
  overflow-y: auto;
`

const chipStyle = css`
  ${paragraphXSmall};
  padding: 4px 10px;
  border-radius: 6px;
  background: ${colors.SECONDARY_100};
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;
`

const overflowChipStyle = css`
  background: transparent;
  border: 1px dashed ${colors.SECONDARY_100};
  color: ${colors.NEUTRAL_70};
`

/**
 * Two columns like the feed composer's three, so a channel and its hint read as
 * one block instead of a stacked list of sentences.
 */
const channelGridStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const hintStyle = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
`
